import { Operation } from '../models/operation.model';
import { Timestamp } from './timestamp';

export type MerkleTrie =
    | {
          [key: number]: MerkleTrie;
          hash: number;
          hasAdd?: boolean;
      }
    | Record<string | number, never>;

function getKeys(trie: MerkleTrie) {
    return trie
        ? Object.keys(trie)
            .filter((x) => x !== 'hash' && x !== 'hasAdd')
            .map(Number)
        : [];
}

function keyToTimestamp(key: string) {
    // 16 is the length of the base 3 value of the current time in
    // minutes. Ensure it's padded to create the full value
    const fullkey = key + '0'.repeat(16 - key.length);

    // Parse the base 3 representation
    return parseInt(fullkey, 3) * 1000 * 60;
}

function insert(trie: MerkleTrie, timestamp: Timestamp, opType?: Operation['type']) {
    const hash = timestamp.hash();
    const key = Number((timestamp.millis() / 1000 / 60) | 0).toString(3);
    insertKey(trie, key, hash, timestamp, opType);
    return trie;
}

function insertKey(trie: MerkleTrie, key: string, hash: number, timestamp: Timestamp, opType?: Operation['type']): MerkleTrie {
    if (key.length === 0) {
        return trie;
    }
    const childKey = Number(key[0]);
    if (!trie[childKey]) {
        trie[childKey] = {};
    }
    const currChild = trie[childKey];

    const nextKey = key.slice(1);
    insertKey(currChild, nextKey, hash, timestamp, opType);
    trie.hash = trie.hash ^ hash;
    trie.hasAdd = trie.hasAdd || opType === 'addDoc';
}

function build(timestamps: Timestamp[]) {
    const trie: MerkleTrie = {};
    for (const timestamp of timestamps) {
        insert(trie, timestamp);
    }
    return trie;
}

function diff(trie1: MerkleTrie, trie2: MerkleTrie): number | null {
    if (trie1.hash === trie2.hash) {
        return null;
    }

    let node1 = trie1;
    let node2 = trie2;
    let k = '';

    // eslint-disable-next-line no-constant-condition
    while (true) {
        const keyset = new Set([...getKeys(node1), ...getKeys(node2)]);
        const keys = [...keyset.values()];
        keys.sort();

        const diffkey = keys.find((key) => {
            const next1 = node1[key] || {};
            const next2 = node2[key] || {};
            return next1.hash !== next2.hash;
        });

        if (!diffkey) {
            return keyToTimestamp(k);
        }

        k += diffkey;
        node1 = node1[diffkey] || {};
        node2 = node2[diffkey] || {};
    }
    return null;
}

function getLatestKey(trie: MerkleTrie) {
    let k = '';

    // eslint-disable-next-line no-constant-condition
    while (true) {
        const keys = getKeys(trie);
        keys.sort();
        const biggestKey = keys[keys.length - 1];

        if (!biggestKey) {
            return keyToTimestamp(k);
        }

        k += biggestKey;
        trie = trie[biggestKey] || {};
    }
}

function prune(trie: MerkleTrie, n = 2) {
    // Do nothing if empty
    if (!trie.hash) {
        return trie;
    }

    const keys = getKeys(trie);
    keys.sort();

    const next: MerkleTrie = { hash: trie.hash };
    keys.slice(-n).forEach((k) => (next[k] = prune(trie[k], n)));

    return next;
}

function debug(trie: MerkleTrie, k: number | null = null, indent = 0): string {
    const str =
        ' '.repeat(indent) + (k !== null ? `k: ${k} ` : '') + `hash: ${trie.hash || '(empty)'}\n`;
    return (
        str +
        getKeys(trie)
            .map((key) => {
                return debug(trie[key], key, indent + 2);
            })
            .join('')
    );
}

export { insert, diff, build, getLatestKey };
